/** @file IK tool: drag an IK target (or a chain end's target); a click on another bone creates its IK. */
import { pickAt } from '../../panels/viewport/pickAt';
import type { ToolContext, ToolGesture, ToolPointer } from '../../panels/viewport/viewportTypes';
import { updateSelection } from '../../stores/updateSelection';
import { chainRotations } from '../transform/chainRotations';
import { editTargetOf } from '../transform/editTargetOf';
import { ikTargetGesture } from '../transform/ikTargetGesture';
import { createIkCommands } from './createIkCommands';
import { ikOfBone } from './ikOfBone';

/** A click gesture whose release creates the IK (Setup only). */
function createGesture(ctx: ToolContext, end: number): ToolGesture | null {
  if (ctx.tool.mode !== 'setup') {
    ctx.notify({ kind: 'info', key: 'viewport.setupOnly' });
    return null;
  }
  return { move() {}, release: () => createIkCommands(ctx, end), cancel() {} };
}

/**
 * R-E1-13; the pressed bone becomes the selection; a press on nothing does nothing. A target drag works as grab:
 * Setup also commits the chain's solved rotations, Animate keys the target only (the tool has no wrist).
 */
export const ikPointer: ToolPointer = {
  press(ctx, p) {
    const hit = pickAt(ctx, p.sx, p.sy);
    if (hit.bone < 0) return null;
    const id = ctx.maps.boneIds[hit.bone] ?? '';
    updateSelection({ bones: [id], slots: [] }, 'replace');
    const ik = ikOfBone(ctx, id);
    if (ik === null) return createGesture(ctx, hit.bone);
    const edit = editTargetOf(ctx);
    if (edit === null) return null;
    return ikTargetGesture(ctx, edit, ik, p, edit.mode === 'setup' ? chainRotations(ctx, ik) : undefined);
  },
  idle: () => null,
};
